import React, { memo, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors, NetworkErrors } from 'context';
import { appState, isCareerViewer, useGrowth, useMyCareerCenter } from 'core';
import { authState } from 'core/state';
import { PATHS } from 'router/config';

import { CareerPreviewNotice } from 'components/pages/CareerCenter';
import { GrowthAchievements as MedalGrid } from 'components/pages/Growth/parts/GrowthAchievements';

/**
 * /growth/achievements — «Баллы и медали» (ТЗ 0039, доводка UX).
 *
 * Отдельная страница появилась по прямой претензии владельца: «у нас есть
 * здесь оценки — вынести на отдельную страницу, расписать, для чего это вообще
 * всё нужно. Сейчас ты ученик, а потом что?»
 *
 * Ответ у системы был и раньше: пять уровней заданы кортежем GROWTH_LEVELS,
 * потолок — 315 очков. Показывалась ровно одна ступень, поэтому лестница
 * выглядела тупиком.
 *
 * 🔴 Очки НИЧЕГО НЕ ЗАПИРАЮТ — так решил владелец: «награды, а не запреты».
 * Вакансии, курсы и карта навыков открыты всем участникам независимо от
 * уровня. Поэтому текст обещает скидку и статус, а не доступ: обещать доступ,
 * которого механизм не отнимает, значит соврать на первом же экране.
 */
export const GrowthMedals = memo(() => {
  const { t } = useTranslation();
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const { growth, loading } = useGrowth();
  const { isPreview, loading: centerLoading } = useMyCareerCenter();

  // Тот же гард, что на /growth: раздел открыт студенту и методисту-смотрящему,
  // остальным — 404, а не полупустой экран с молчаливыми отказами.
  const currentState = appState();
  useEffect(() => {
    if (!isCareerViewer(active_role) || isDemoUser) {
      appState({ ...currentState, networkError: NetworkErrors.pageIsNotAvailable });
    }
  }, [active_role, isDemoUser, currentState]);

  const earned = useMemo(
    () => (growth?.achievements ?? []).filter((row) => row.earned).length,
    [growth],
  );

  if ((loading && !growth) || centerLoading) {
    return (
      <Center>
        <Spin size="large" />
      </Center>
    );
  }

  if (!growth) {
    return (
      <PageWrapper>
        {isPreview ? <CareerPreviewNotice /> : null}
        <Title>{t('growth.medals.title')}</Title>
        <Muted>{t('growth.empty')}</Muted>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      {isPreview ? <CareerPreviewNotice /> : null}

      <Breadcrumb to={PATHS.growth}>{t('growth.backToHub')}</Breadcrumb>

      <header>
        <Title>{t('growth.medals.title')}</Title>
        <Lead>{t('growth.medals.lead')}</Lead>
      </header>

      <Summary>
        <Figure>
          <FigureValue>{growth.xp}</FigureValue>
          <FigureLabel>{t('growth.medals.xpLabel')}</FigureLabel>
        </Figure>
        <Figure>
          <FigureValue>
            {earned}
            <Slash>/{growth.availableTotal}</Slash>
          </FigureValue>
          <FigureLabel>{t('growth.medals.medalsLabel')}</FigureLabel>
        </Figure>
        <Figure>
          <FigureValue>
            {t(`growth.level.${growth.level}`, { defaultValue: growth.levelTitle })}
          </FigureValue>
          <FigureLabel>{t('growth.medals.levelLabel')}</FigureLabel>
        </Figure>
      </Summary>

      <section>
        <SectionTitle>{t('growth.medals.ladderTitle')}</SectionTitle>
        <SectionLead>{t('growth.medals.ladderLead')}</SectionLead>
        <Ladder>
          {growth.levels.map((level) => {
            const isCurrent = level.index === growth.level;
            return (
              <Step key={level.index} reached={level.reached} current={isCurrent}>
                <Rung reached={level.reached} current={isCurrent}>
                  {level.index + 1}
                </Rung>
                <StepBody>
                  <StepName>
                    {t(`growth.level.${level.index}`, { defaultValue: level.title })}
                  </StepName>
                  <StepThreshold>{t('growth.medals.from', { n: level.threshold })}</StepThreshold>
                  {isCurrent ? <Here>{t('growth.medals.youAreHere')}</Here> : null}
                  {/* Подсказка «сколько осталось» — только на СЛЕДУЮЩЕЙ ступени.
                      На каждой недостигнутой она превращала лестницу в столбик
                      чисел, из которого не видно ближайшей цели. */}
                  {!level.reached && level.index === growth.level + 1 ? (
                    <ToGo>{t('growth.medals.toGo', { n: level.threshold - growth.xp })}</ToGo>
                  ) : null}
                </StepBody>
              </Step>
            );
          })}
        </Ladder>
      </section>

      <Explain>
        <SectionTitle>{t('growth.medals.whyTitle')}</SectionTitle>
        <Reasons>
          <Reason>
            <ReasonTitle>{t('growth.medals.why1Title')}</ReasonTitle>
            <ReasonText>{t('growth.medals.why1Text')}</ReasonText>
          </Reason>
          <Reason>
            <ReasonTitle>{t('growth.medals.why2Title')}</ReasonTitle>
            <ReasonText>{t('growth.medals.why2Text')}</ReasonText>
          </Reason>
          <Reason>
            <ReasonTitle>{t('growth.medals.why3Title')}</ReasonTitle>
            <ReasonText>{t('growth.medals.why3Text')}</ReasonText>
          </Reason>
        </Reasons>
        {/* Прямо и без обиняков: очки не отнимают доступ. Иначе студент будет
            искать, что ему «закрыли», и не найдёт. */}
        <NoGate>{t('growth.medals.noGate')}</NoGate>
      </Explain>

      <MedalGrid
        achievements={growth.achievements}
        isCareerMember={growth.isCareerMember}
        availableTotal={growth.availableTotal}
        lockedTotal={growth.lockedTotal}
      />
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  padding: 40px 0 80px;
  display: flex;
  flex-direction: column;
  gap: 28px;

  ${mobile} {
    padding: 20px 0 48px;
  }
`;

const Breadcrumb = styled(Link)`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}

  &:hover {
    color: ${Colors.black};
  }
`;

const Title = styled.h1`
  margin: 0;
  ${fontService.getFont({ size: 32, lineHeight: 40, weight: FontWeight.bold })}
`;

const Lead = styled.p`
  margin: 8px 0 0;
  max-width: 720px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Figure = styled.div`
  padding: 18px 20px;
  border-radius: 12px;
  background: ${Colors.bg_gray};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FigureValue = styled.span`
  ${fontService.getFont({ size: 24, lineHeight: 30, weight: FontWeight.bold })}
`;

const Slash = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.getFont({ size: 16, lineHeight: 22, weight: FontWeight.medium })}
`;

const FigureLabel = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const SectionTitle = styled.h2`
  margin: 0;
  ${fontService.getFont({ size: 20, lineHeight: 26, weight: FontWeight.bold })}
`;

const SectionLead = styled.p`
  margin: 6px 0 16px;
  max-width: 720px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Ladder = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
`;

const Step = styled.li<{ reached: boolean; current: boolean }>`
  display: flex;
  gap: 16px;
  padding: 0 0 20px;
  position: relative;

  /* Вертикальная линия лестницы: рисуется псевдоэлементом от ступени вниз,
     у последней — обрезается, иначе линия висит в пустоте. */
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 19px;
    top: 40px;
    bottom: 0;
    width: 2px;
    background: ${({ reached }) => (reached ? Colors.marker_green : Colors.gray)};
  }
`;

const Rung = styled.span<{ reached: boolean; current: boolean }>`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Текущая ступень — жёлтая ЗАЛИВКА и btn_text поверх (правило в
     context/constants/colors.ts: black в тёмной теме равен #ECECEC и на
     жёлтом исчезает).

     Пройденная — зелёная РАМКА и обычный текст, а не заливка с белым: белый
     на marker_green читается в светлой теме и почти пропадает в тёмной, где
     тот же токен светлеет до #12B577. Рамка от темы не зависит. */
  background: ${({ current }) => (current ? Colors.main : Colors.bg_gray)};
  color: ${({ reached, current }) =>
    current ? Colors.btn_text : reached ? Colors.black : Colors.gray_dark};
  border: 1px solid
    ${({ reached, current }) =>
      current ? 'transparent' : reached ? Colors.signal_green : Colors.gray};
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 6px;
`;

const StepName = styled.span`
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const StepThreshold = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Here = styled.span`
  margin-top: 4px;
  align-self: flex-start;
  padding: 2px 10px;
  border-radius: 12px;
  background: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const ToGo = styled.span`
  margin-top: 4px;
  color: ${Colors.marker_green};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Explain = styled.section`
  padding: 24px;
  border-radius: 12px;
  border: 1px solid ${Colors.gray};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Reasons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Reason = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReasonTitle = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const ReasonText = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const NoGate = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Muted = styled.p`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px 0;
`;
